import { useEffect, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { Modal } from '../../Modal';
import { Button, IconButton, Typography } from '../ui';
import { devToolsUnlocked } from '../../../lib/dev-tools';
import { useRecipient } from '../../settings/shared/settings-queries';
import { useActiveConversation } from '../../chat/ActiveConversationContext';
import styles from './DevDebugInfoPanel.module.css';

// The web port of the native apps' Dev Tools ▸ "Share debug info" row (see native `DebugShare.swift` /
// `DebugShare.kt`) — same three fields (email, metaSiteId, active conversation id) plus the derived
// Symphony Live monitoring URL. Native hands that text to the OS share sheet; a browser has no share
// sheet, so this opens a modal instead and lets you copy each field, or everything together — the web
// idiom for the same "get this to whoever needs it" job.

const MONITORING_BASE_URL = 'https://bo.wix.com/pages/symphony-live/conversation-monitoring';

/** Gate #3 (BrandHeader and DevMenu check it too): never renders without a dev build or a grant. */
export function DevDebugInfoPanel(props: { open: boolean; onClose: () => void }) {
  if (!devToolsUnlocked()) return null;
  return <DebugInfoPanel {...props} />;
}

function DebugInfoPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: recipient } = useRecipient();
  const { activeConversationId } = useActiveConversation();
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Transient, like the share-link copy hook — a stale "Copied" must not outlive the confirmation.
  useEffect(() => {
    if (!copiedKey) return undefined;
    const timer = window.setTimeout(() => setCopiedKey(null), 2000);
    return () => window.clearTimeout(timer);
  }, [copiedKey]);

  const email = recipient?.email || '-';
  const metaSiteId = recipient?.metaSiteId || '-';
  const conversationId = activeConversationId || '-';
  const monitoringUrl =
    recipient?.metaSiteId && activeConversationId
      ? `${MONITORING_BASE_URL}?metaSiteId=${encodeURIComponent(recipient.metaSiteId)}&conversationId=${encodeURIComponent(activeConversationId)}`
      : '-';

  const rows: { key: string; label: string; value: string }[] = [
    { key: 'email', label: 'Email', value: email },
    { key: 'metaSiteId', label: 'Meta site id', value: metaSiteId },
    { key: 'conversationId', label: 'Active conversation id', value: conversationId },
    { key: 'monitoringUrl', label: 'Conversation monitoring URL', value: monitoringUrl },
  ];

  const allText = rows.map((row) => `${row.label}: ${row.value}`).join('\n');

  /**
   * Whether the report says anything at all. Copy all deliberately KEEPS a placeholder row — native's
   * `debugText()` prints "-" for a value it does not have, and that dash is signal rather than noise:
   * "no conversation open" is exactly what the reader of a pasted report needs to know, and a blob that
   * silently omits the field cannot say it was absent. What it must not do is report success with
   * nothing real in it (the recipient read still in flight AND no conversation), which is the per-field
   * rule applied to the whole report rather than a contradiction of it.
   */
  const hasAnyValue = rows.some((row) => row.value !== '-');

  const copy = async (key: string, value: string) => {
    if (value === '-') return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
    } catch {
      // Clipboard denied — every value is plain selectable text, so nothing else to do.
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={560}
      title="Share debug info"
      footer={
        <Button variant="outlined" disabled={!hasAnyValue} onClick={() => copy('all', allText)}>
          {copiedKey === 'all' ? 'Copied' : 'Copy all'}
        </Button>
      }
    >
      <Typography variant="p3" color="secondary">
        The same fields the native apps&apos; Dev Tools ▸ Share debug info sends to the OS share sheet —
        copy one field, or everything together.
      </Typography>
      <div className={styles.rows}>
        {rows.map((row) => (
          <div key={row.key} className={styles.row}>
            <div className={styles.labels}>
              <Typography variant="p3" color="muted" component="div">
                {row.label}
              </Typography>
              <Typography variant="p3" color="strong" component="div" className={styles.value}>
                {row.value}
              </Typography>
            </div>
            {/* Icon-only, so the DS square sibling rather than a Button — it carries the label. */}
            <IconButton
              variant="outlined"
              size="small"
              aria-label={copiedKey === row.key ? `${row.label} copied` : `Copy ${row.label.toLowerCase()}`}
              disabled={row.value === '-'}
              onClick={() => copy(row.key, row.value)}
            >
              {copiedKey === row.key ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
            </IconButton>
          </div>
        ))}
      </div>
    </Modal>
  );
}
